import { getGPUTier, TierResult } from 'detect-gpu';
import MobileDetect from 'mobile-detect';
import * as res from 'res';
import { UAParser } from 'ua-parser-js';
import EE from '~/listenerEventService/EventEmitter';
import { clickstreamLogger } from '~/logging';

export default class UserAgentListener extends EE {
  extendedNavigator = window.navigator as typeof window.navigator & {
    deviceMemory: number;
    getBattery: () => Promise<{
      level: number;
      charging: boolean;
      chargingTime: number;
    }>;
  };

  getUserAgent = () => {
    const parser = new UAParser();

    return parser.getResult();
  }

  detectUserAgentInfo = () => {
    const userAgentInfo = this.getUserAgent();
    this.emit('analytics', {
      type: 'user-agent-info',
      ...userAgentInfo,
    });
  }
  // Определяем включен ли на сайте AdBlock;
  detectIsAdBlockEnabled = () => {
    let adBlockEnabled = false;
    const testAd = document.createElement('div');
    testAd.innerHTML = '&nbsp;';
    testAd.className = 'adsbox';
    document.body.appendChild(testAd);

    window.setTimeout(() => {
      if (testAd.offsetHeight === 0) {
        adBlockEnabled = true;
      }
      testAd.remove();
      this.emit('analytics', {
        type: 'is-adblock-enabled',
        value: adBlockEnabled.toString(),
      });
    }, 100);
  }

  // Определяем pixel ratio.
  detectPixelRatio() {
    const pixelRatio = {
      dpi: res.dpi(),
      dpcm: res.dpcm(),
      dppx: res.dppx(),
    };

    this.emit('analytics', {
      type: 'pixel-ratio',
      ...pixelRatio,
    });
  }

  // Получаем информацию по устройству.
  detectDevice = () => {
    const md = new MobileDetect(window.navigator.userAgent);

    /**
     * @prop phone Returns the detected phone type/family string or null;
     * @prop mobile Returns the detected phone or tablet type or null if it is not a mobile device.
     * @prop tablet Returns the detected tablet type/family string or null.
     * @prop mobileGrade Returns the mobile grade ('A', 'B', 'C').
     * @prop os Returns the detected operating system string or null.
     * @prop userAgent Returns the (first) detected user-agent string or null.
     * @prop versionOS Get the version (as String) based on {os} property.
     * @prop versionUserAgent Get the version (as String) based on {userAgent} property.
     */
    const deviceInfo = {
      phone: md.phone(),
      mobile: md.mobile(),
      tablet: md.tablet(),
      mobileGrade: md.mobileGrade(),
      os: {
        name: md.os(),
        version: md.versionStr(md.os()),
      },
      ua: md.userAgent(),
      browser: {
        version: md.versionStr(md.userAgent()),
      },
    };

    this.emit('analytics', {
      type: 'device-info',
      ...deviceInfo,
    });
  }
  // Определяем разрешение экрана/монитора.
  detectScreenResolution = () => {
    const screenResolution = {
      width: window.screen.width * window.devicePixelRatio,
      height: window.screen.height * window.devicePixelRatio,
    };

    this.emit('analytics', {
      type: 'screen-resolution',
      ...screenResolution,
    });
  }

  // Определяем количество ядер и архитектуру процессора.
  detectCPU = () => {
    const cpuInfo = {
      cores: window.navigator.hardwareConcurrency || null,
      architecture: this.getUserAgent().cpu.architecture || null,
    };

    this.emit('analytics', {
      type: 'cpu-info',
      cpu: cpuInfo,
    });
  }

  // Определяем периферийные устройства.
  detectMediaDevices = () => {
    if (
      !window.navigator.mediaDevices ||
      !window.navigator.mediaDevices.enumerateDevices
    )
      return;

    let mediaDevices: MediaDeviceInfo[];
    (async () => {
      mediaDevices = await window.navigator.mediaDevices.enumerateDevices();
    })();

    const mediaDevicesInfo = mediaDevices?.reduce(
      (devices, device) => {
        switch (device.kind) {
          case 'audioinput':
            devices.microphones++;
            break;
          case 'videoinput':
            devices.webCameras++;
            break;
          case 'audiooutput':
            devices.speakers++;
            break;
        }

        return devices;
      },
      {
        microphones: 0,
        webCameras: 0,
        speakers: 0,
      }
    ) || {};

    this.emit('analytics', {
      type: 'media-devices',
      ...mediaDevicesInfo,
    });
  }

  // Определяем оперативную память.
  detectRAM = () => {
    this.emit('analytics', {
      type: 'ram',
      value: this.extendedNavigator.deviceMemory
        ? this.extendedNavigator.deviceMemory.toString()
        : '0',
    });
  }

  // Определяем батарею.
  detectBattery = () => {
    if (this.extendedNavigator.getBattery) {
      let batteryManager: { level: number; charging: boolean; chargingTime: number };
      (async () => {
        batteryManager = await this.extendedNavigator.getBattery();
      })();

      const batteryInfo = {
        level: batteryManager.level * 100 + '%',
        isCharging: batteryManager.charging,
      };

      this.emit('analytics', {
        type: 'battery-info',
        battery: batteryInfo,
      });
    }
  }

  // Определяем ориентацию экрана.
  detectScreenOrientation = () => {
    this.emit('analytics', {
      type: 'screen-orientation',
      value: window.screen.orientation.type,
    });
  }

  // Определяем глубину цвета.
  detectColorDepth = () => {
    this.emit('analytics', {
      type: 'color-depth',
      value: window.screen.colorDepth.toString(),
    });
  }

  // Определяем размер окна браузера.
  detectBrowserWindowSize = () => {
    const browserWindowSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    this.emit('analytics', {
      type: 'browser-window-size',
      ...browserWindowSize,
    });
  }

  // Опредеяем наличие touchscreen.
  detectTouchscreen = () => {
    this.emit('analytics', {
      type: 'touchscreen-support',
      value: (navigator.maxTouchPoints > 1).toString(),
    });
  }

  // Определяем доступность cookies.
  detectCookiesStatus = () => {
    this.emit('analytics', {
      type: 'cookies-status',
      value: window.navigator.cookieEnabled.toString(),
    });
  }

  // Определяем видеокарту.
  detectGraphics = async () => {
    let gpuTier: TierResult;

    try {
      gpuTier = await getGPUTier();
    } catch (error) {
      clickstreamLogger.error('An error occurred while determining the video card: ', error);
    }

    this.emit('analytics', {
      type: 'graphics',
      value: gpuTier.gpu,
    });
  }

  // Определяем язык браузера.
  detectBrowserLanguage = () => {
    this.emit('analytics', {
      type: 'browser-language',
      value: window.navigator.language,
    });
  }

  // Определяем тип устройства.
  detectDeviceType = () => {
    const getDeviceType = () => {
      const ua = window.navigator.userAgent;

      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua))
        return 'tablet';

      if (
        /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
          ua
        )
      )
        return 'mobile';

      if (this.extendedNavigator.getBattery) {
        let batteryManager: { level: number; charging: boolean; chargingTime: number };
        (async () => {
          batteryManager = await this.extendedNavigator.getBattery();
        })();
        if (batteryManager.charging && batteryManager.chargingTime === 0)
          return 'desktop';
      }

      return 'laptop';
    };

    this.emit('analytics', {
      type: 'device-type',
      value: getDeviceType(),
    });
  }

  // Определяем скорость загрузки страницы.
  detectPageLoadTime = () => {
    const pageLoadTime =
      window.performance.timing.domContentLoadedEventEnd -
      window.performance.timing.navigationStart;

    this.emit('analytics', {
      type: 'page-load-time',
      value: pageLoadTime.toString(),
    });
  }

  start() {
    clickstreamLogger.info('start userinfo');

    this.detectUserAgentInfo();
    this.detectIsAdBlockEnabled();
    this.detectPixelRatio();
    this.detectDevice();
    this.detectScreenResolution();
    this.detectCPU();
    this.detectMediaDevices();
    this.detectRAM();
    this.detectBattery();
    this.detectScreenOrientation();
    this.detectColorDepth();
    this.detectBrowserWindowSize();
    this.detectTouchscreen();
    this.detectCookiesStatus();
    this.detectGraphics();
    this.detectBrowserLanguage();
    this.detectDeviceType();
    this.detectPageLoadTime();
  }

  // tslint:disable-next-line:no-empty
  stop() {
  }
}
